import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import Select from 'react-select';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';
import WorkLogChart from '../../components/WorkLogChart';

const SELECT_STYLES = {
  control: (base, state) => ({
    ...base,
    borderRadius: '0.5rem',
    borderColor: state.isFocused ? '#818cf8' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px #a5b4fc' : 'none',
    fontSize: '0.875rem',
    minHeight: '38px',
    '&:hover': { borderColor: '#818cf8' },
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '0.875rem',
    backgroundColor: state.isSelected ? '#4f46e5' : state.isFocused ? '#eef2ff' : 'white',
    color: state.isSelected ? 'white' : '#111827',
  }),
  menu: (base) => ({ ...base, borderRadius: '0.5rem', zIndex: 50 }),
};

function todayLocalString() {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function currentMonthString() {
  return todayLocalString().slice(0, 7);
}

function to12h(time24) {
  if (!time24) return '';
  const [h, m] = time24.split(':');
  let hours = parseInt(h, 10);
  const modifier = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}:${m} ${modifier}`;
}

const STATUS_META = {
  pending: { label: 'Pending', badge: 'bg-gray-100 text-gray-700', border: 'border-l-gray-400' },
  in_process: { label: 'In Progress', badge: 'bg-blue-100 text-blue-700', border: 'border-l-blue-400' },
  completed: { label: 'Completed', badge: 'bg-green-100 text-green-700', border: 'border-l-green-500' },
};

const EMPTY_FORM = { title: '', description: '', categoryId: null, startTime: '', endTime: '', status: 'pending' };

// Categories flagged "pin last" (e.g. "Others") always sort after everything
// else, alphabetical within each group -- the backend already returns them
// pre-sorted this way, but re-sorting here too is cheap insurance.
function sortCategories(categories) {
  return [...categories].sort((a, b) => (a.pinLast === b.pinLast ? a.name.localeCompare(b.name) : a.pinLast ? 1 : -1));
}

const CONFIRM_COMPLETE_OPTS = {
  icon: 'warning',
  title: 'Mark this entry as completed?',
  text: "Once completed, it can never be edited or deleted again — not even the status. This can't be undone.",
  showCancelButton: true,
  confirmButtonText: 'Yes, mark complete',
  confirmButtonColor: '#16a34a',
  cancelButtonText: 'Cancel',
};

function LockIcon({ className }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 17a2 2 0 100-4 2 2 0 000 4zm6-9h-1V6a5 5 0 00-10 0v2H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V10a2 2 0 00-2-2zM8.9 6a3.1 3.1 0 016.2 0v2H8.9V6z" />
    </svg>
  );
}

function EntryCard({ entry, onEdit, onDelete, onMarkComplete }) {
  const isPlanned = !entry.status; // future-dated entries carry no status yet
  const meta = isPlanned
    ? { label: 'Planned', badge: 'bg-purple-100 text-purple-700', border: 'border-l-purple-400' }
    : STATUS_META[entry.status] || STATUS_META.pending;

  return (
    <div className={`rounded-xl border border-gray-200 border-l-4 ${meta.border} bg-white p-4 shadow-sm ${entry.isLocked ? 'bg-gray-50/70' : ''}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-gray-900">{entry.title}</h3>
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold ${meta.badge}`}>
              {entry.isLocked && <LockIcon className="h-3 w-3" />}
              {isPlanned ? 'Planned' : meta.label}
            </span>
            {entry.categoryName && (
              <span className="rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-semibold text-indigo-700">{entry.categoryName}</span>
            )}
          </div>
          {entry.description && <p className="mt-1 whitespace-pre-wrap text-sm text-gray-600">{entry.description}</p>}
          {!isPlanned && entry.startTime && (
            <p className="mt-1.5 text-xs font-medium text-gray-500">
              {to12h(entry.startTime)} – {to12h(entry.endTime)} · {entry.durationHours}h
            </p>
          )}
          {entry.isLocked && (
            <p className="mt-1.5 flex items-center gap-1 text-xs font-medium text-gray-400">
              <LockIcon className="h-3 w-3" /> Completed — locked, can no longer be changed
            </p>
          )}
        </div>
        {!entry.isLocked && (
          <div className="flex shrink-0 flex-wrap items-center gap-1.5">
            {!isPlanned && entry.status !== 'completed' && (
              <button
                type="button"
                onClick={() => onMarkComplete(entry)}
                className="rounded-lg border border-green-200 bg-green-50 px-2.5 py-1.5 text-xs font-semibold text-green-700 hover:bg-green-100"
              >
                Mark complete
              </button>
            )}
            <button
              type="button"
              onClick={() => onEdit(entry)}
              className="rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => onDelete(entry)}
              className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100"
            >
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function EntryFormModal({ isFuture, formData, setFormData, formDate, setFormDate, duration, categoryOptions, isSuperAdmin, errors, submitting, isEditing, onSubmit, onCancel }) {
  const selectedCategory = categoryOptions.find((opt) => opt.value === formData.categoryId) || null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
        <h2 className="text-lg font-bold text-gray-900">{isEditing ? 'Edit entry' : 'Add entry'}</h2>
        <form onSubmit={onSubmit} className="mt-4 space-y-4">
          <div>
            <label className="mb-1 block text-sm font-semibold text-gray-700">Date</label>
            <input
              type="date"
              value={formDate}
              onChange={(e) => setFormDate(e.target.value)}
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold text-gray-700">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData((f) => ({ ...f, title: e.target.value }))}
              required
              maxLength={255}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold text-gray-700">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))}
              rows={3}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="block text-sm font-semibold text-gray-700">Category</label>
              {isSuperAdmin && (
                <a href="/admin/work-log-categories" target="_blank" rel="noreferrer" className="text-xs font-semibold text-indigo-600 hover:text-indigo-700">
                  Manage categories
                </a>
              )}
            </div>
            <Select
              options={categoryOptions}
              value={selectedCategory}
              onChange={(opt) => setFormData((f) => ({ ...f, categoryId: opt ? opt.value : null }))}
              placeholder="Select a category…"
              classNamePrefix="rs"
              styles={SELECT_STYLES}
            />
          </div>

          {isFuture ? (
            <p className="rounded-lg bg-purple-50 px-3 py-2 text-xs text-purple-700">
              This is a future date — only a title and description are needed. You&rsquo;ll be able to add start/end time and mark it complete once the day arrives.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-sm font-semibold text-gray-700">Start time</label>
                  <input
                    type="time"
                    value={formData.startTime}
                    onChange={(e) => setFormData((f) => ({ ...f, startTime: e.target.value }))}
                    required
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-semibold text-gray-700">End time</label>
                  <input
                    type="time"
                    value={formData.endTime}
                    onChange={(e) => setFormData((f) => ({ ...f, endTime: e.target.value }))}
                    required
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>
              {duration && <p className="text-xs font-medium text-gray-500">Duration: {duration} hours</p>}
              <div>
                <label className="mb-1 block text-sm font-semibold text-gray-700">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData((f) => ({ ...f, status: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="pending">Pending</option>
                  <option value="in_process">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </>
          )}

          {errors.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {errors.map((err) => <p key={err}>{err}</p>)}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function MyWorkLog() {
  const isSuperAdmin = Boolean(AuthService.getUser()?.superAdmin?.superAdminId);

  const [selectedDate, setSelectedDate] = useState(todayLocalString());
  const [entries, setEntries] = useState([]);
  const [serverToday, setServerToday] = useState(null);
  const [isFutureDate, setIsFutureDate] = useState(false);
  const [loadingEntries, setLoadingEntries] = useState(true);

  const [categories, setCategories] = useState([]);

  const [chartMonth, setChartMonth] = useState(currentMonthString());
  const [chartDays, setChartDays] = useState([]);
  const [attentionCount, setAttentionCount] = useState(0);
  const [loadingChart, setLoadingChart] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null); // null = add mode
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formDate, setFormDate] = useState(selectedDate);
  const [formIsFuture, setFormIsFuture] = useState(false);
  const [formErrors, setFormErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const loadEntries = useCallback(async (date) => {
    setLoadingEntries(true);
    try {
      const res = await axiosInstance.get('/admin/my-work-log', { params: { date } });
      setEntries(res.data?.entries || []);
      setServerToday(res.data?.serverToday || null);
      setIsFutureDate(!!res.data?.isFutureDate);
    } catch (err) {
      setEntries([]);
      Swal.fire('Error', err?.response?.data?.message || 'Failed to load work log entries.', 'error');
    } finally {
      setLoadingEntries(false);
    }
  }, []);

  const loadChart = useCallback(async (month) => {
    setLoadingChart(true);
    try {
      const res = await axiosInstance.get('/admin/my-work-log/summary', { params: { month } });
      setChartDays(res.data?.days || []);
      setAttentionCount(res.data?.needsAttentionCount || 0);
      if (res.data?.serverToday) setServerToday(res.data.serverToday);
    } catch {
      setChartDays([]);
    } finally {
      setLoadingChart(false);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/admin/work-log-categories');
      setCategories(sortCategories(res.data?.categories || []));
    } catch {
      setCategories([]);
    }
  }, []);

  useEffect(() => { loadEntries(selectedDate); }, [selectedDate, loadEntries]);
  useEffect(() => { loadChart(chartMonth); }, [chartMonth, loadChart]);
  useEffect(() => { loadCategories(); }, [loadCategories]);

  const categoryOptions = useMemo(
    () => categories.map((c) => ({ value: c.categoryId, label: c.name })),
    [categories]
  );

  // Live client-side approximation only, purely for form UX responsiveness
  // as the user edits the date field -- the server is the real authority
  // and re-derives this from its own clock on submit regardless.
  useEffect(() => {
    if (!serverToday || !formDate) return;
    setFormIsFuture(formDate > serverToday);
  }, [formDate, serverToday]);

  const openAddForm = () => {
    setEditingEntry(null);
    setFormData(EMPTY_FORM);
    setFormDate(selectedDate);
    setFormIsFuture(isFutureDate);
    setFormErrors([]);
    setShowForm(true);
  };

  const openEditForm = (entry) => {
    setEditingEntry(entry);
    setFormData({
      title: entry.title || '',
      description: entry.description || '',
      categoryId: entry.categoryId || null,
      startTime: entry.startTime || '',
      endTime: entry.endTime || '',
      status: entry.status || 'pending',
    });
    setFormDate(entry.date);
    setFormIsFuture(serverToday ? entry.date > serverToday : false);
    setFormErrors([]);
    setShowForm(true);
  };

  const closeForm = () => setShowForm(false);

  const refreshAll = async () => {
    await Promise.all([loadEntries(selectedDate), loadChart(chartMonth)]);
  };

  const submitForm = async (statusOverride) => {
    setFormErrors([]);
    if (!formData.categoryId) {
      setFormErrors(['Category is required.']);
      return;
    }
    const payload = {
      date: formDate,
      title: formData.title,
      description: formData.description,
      categoryId: formData.categoryId,
      ...(formIsFuture ? {} : {
        startTime: formData.startTime,
        endTime: formData.endTime,
        status: statusOverride || formData.status,
      }),
    };

    setSubmitting(true);
    try {
      if (editingEntry) {
        await axiosInstance.put(`/admin/my-work-log/${editingEntry.workLogId}`, payload);
      } else {
        await axiosInstance.post('/admin/my-work-log', payload);
      }
      setShowForm(false);
      await refreshAll();
    } catch (err) {
      const errors = err?.response?.data?.errors;
      if (Array.isArray(errors) && errors.length) {
        setFormErrors(errors);
      } else {
        Swal.fire('Error', err?.response?.data?.message || 'Failed to save this entry.', 'error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const wasCompleted = editingEntry?.status === 'completed';
    const transitioningToCompleted = !formIsFuture && formData.status === 'completed' && !wasCompleted;

    if (transitioningToCompleted) {
      Swal.fire(CONFIRM_COMPLETE_OPTS).then((result) => {
        if (result.isConfirmed) submitForm('completed');
      });
      return;
    }
    submitForm();
  };

  const handleMarkComplete = async (entry) => {
    const result = await Swal.fire(CONFIRM_COMPLETE_OPTS);
    if (!result.isConfirmed) return;
    try {
      await axiosInstance.put(`/admin/my-work-log/${entry.workLogId}`, {
        date: entry.date,
        title: entry.title,
        description: entry.description,
        categoryId: entry.categoryId,
        startTime: entry.startTime,
        endTime: entry.endTime,
        status: 'completed',
      });
      await refreshAll();
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to mark this entry as complete.', 'error');
    }
  };

  const handleDelete = async (entry) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete this entry?',
      text: `"${entry.title}" will be permanently removed.`,
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#dc2626',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    try {
      await axiosInstance.delete(`/admin/my-work-log/${entry.workLogId}`);
      await refreshAll();
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to delete this entry.', 'error');
    }
  };

  const duration = useMemo(() => {
    if (formIsFuture || !formData.startTime || !formData.endTime) return null;
    const [sh, sm] = formData.startTime.split(':').map(Number);
    const [eh, em] = formData.endTime.split(':').map(Number);
    if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return null;
    const mins = (eh * 60 + em) - (sh * 60 + sm);
    if (mins <= 0) return null;
    return (mins / 60).toFixed(2);
  }, [formIsFuture, formData.startTime, formData.endTime]);

  return (
    <DashboardLayout title="My Work Log">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My Work Log</h1>
        <p className="mt-1 text-sm text-gray-600">Log what you worked on each day and track it against your schedule.</p>
      </div>

      {attentionCount > 0 && (
        <div className="mb-6 flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          <span>
            You have <strong>{attentionCount}</strong> entr{attentionCount === 1 ? 'y' : 'ies'} still pending or in progress
            {attentionCount === 1 ? " — don't forget to mark it complete once it's done." : " — don't forget to mark them complete once they're done."}
          </span>
        </div>
      )}

      <div className="mb-6">
        {loadingChart ? (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-400">Loading chart…</div>
        ) : (
          <WorkLogChart month={chartMonth} days={chartDays} today={serverToday} onMonthChange={setChartMonth} />
        )}
      </div>

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-sm font-semibold text-gray-700">Date</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
          {serverToday && selectedDate === serverToday && (
            <span className="rounded-full bg-sky-100 px-2.5 py-1 text-xs font-bold text-sky-700">Today</span>
          )}
          {isFutureDate && (
            <span className="rounded-full bg-purple-100 px-2.5 py-1 text-xs font-bold text-purple-700">Future date</span>
          )}
        </div>
        <button
          type="button"
          onClick={openAddForm}
          className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
        >
          + Add entry
        </button>
      </div>

      <div className="space-y-3">
        {loadingEntries ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : entries.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-500">
            No work logged for this date yet.
          </div>
        ) : (
          entries.map((entry) => (
            <EntryCard
              key={entry.workLogId}
              entry={entry}
              onEdit={openEditForm}
              onDelete={handleDelete}
              onMarkComplete={handleMarkComplete}
            />
          ))
        )}
      </div>

      {showForm && (
        <EntryFormModal
          isFuture={formIsFuture}
          formData={formData}
          setFormData={setFormData}
          formDate={formDate}
          setFormDate={setFormDate}
          duration={duration}
          categoryOptions={categoryOptions}
          isSuperAdmin={isSuperAdmin}
          errors={formErrors}
          submitting={submitting}
          isEditing={!!editingEntry}
          onSubmit={handleSubmit}
          onCancel={closeForm}
        />
      )}
    </DashboardLayout>
  );
}
